// src/elements/hudReel/hudReel.ts
import { Z } from "../../utils/constants";
import { HudOptions, HudHandle } from "../hudShared";
import { pauseAllOtherMedia } from "../../utils/mediaUtils";

import { initReelControls } from "./hudReelControls";
import { initAutoplay } from "./hudReelAutoplay";
import { initCaptions } from "./hudReelCaptions";
import { createWakeZone } from "./hudReelWakeZone";

import * as MediaUnlock from "../../utils/mediaUnlock";

import {
  readCcPref,
  applyCcState,
  updateMuteButtonUI,
} from "../../utils/hudState";

function armFirstGestureUnlockOnce(tag: string = "hud-reel") {
  let armed = true;

  const onFirst = () => {
    if (!armed) return;
    try {
      if ((window as any).__SC_HUD_WAKING) {
        return;
      }
    } catch {}
    armed = false;
    try {
      const fn = (MediaUnlock as any).unlockNow ?? (MediaUnlock as any).default?.unlockNow;
      if (typeof fn === "function") {
        fn(tag);
      } else {
        console.warn("[HUDReel] mediaUnlock.unlockNow not found on module export");
      }
    } catch (e) {
      console.warn("[HUDReel] unlockNow failed:", e);
    }
    cleanup();
  };

  const cleanup = () => {
    document.removeEventListener("pointerdown", onFirst, true);
    document.removeEventListener("touchstart", onFirst, true);
    document.removeEventListener("keydown", onFirst, true);
  };

  document.addEventListener("pointerdown", onFirst, true);
  document.addEventListener("touchstart", onFirst, true);
  document.addEventListener("keydown", onFirst, true);

  return cleanup;
}

export function mountReelHUD(opts: {
  section: HTMLElement;
  reelFrame: HTMLElement;
  video: HTMLVideoElement;
  isFirstReel?: boolean;
  hudOptions?: Partial<HudOptions>;
}): HudHandle {
  const { section, reelFrame, video, isFirstReel = false, hudOptions = {} } = opts;

  const controls = document.createElement("div");
  controls.className = "hud-reel-controls";
  Object.assign(controls.style, {
    position: "absolute",
    top: "12px",
    right: "12px",
    zIndex: String(Z.HUD + 1),
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px",
  });
  reelFrame.appendChild(controls);

  try {
    if (!(window as any).__scUnlockArmed) {
      (window as any).__scUnlockDisarm = armFirstGestureUnlockOnce("hud-reel");
      (window as any).__scUnlockArmed = true;
    }
  } catch (e) {
    console.warn("[HUDReel] first-gesture unlock arming failed (non-fatal):", e);
  }

  return attachReelHUD(section, video, controls, {
    ...(hudOptions as any),
    ...(isFirstReel ? { disableAutoplay: true } : {}),
    markAsSeenOnAttach: isFirstReel,
  } as any);
}

export function attachReelHUD(
  section: HTMLElement,
  video: HTMLVideoElement,
  controls: HTMLElement,
  opts: HudOptions = {}
): HudHandle {
  const {
    initialShowMs = 8000,
    wakeShowMs = 10000,
    tapMoveThresholdPx = 10,
    tapMaxMs = 300,
    disableWakeZone = false,
    markAsSeenOnAttach = true,
  } = opts;

  const controlApi = initReelControls(controls, video, section, opts);
  const captionsApi = initCaptions(video, section, controls);

  controlApi.setupLikeButton();
  controlApi.setupMuteButton();
  controlApi.setupCcButton();
  controlApi.setupPausePlayButton();

  updateMuteButtonUI(section, video.muted);

  const handleAutoplayStatus = (ev: Event) => {
    try {
      if ((window as any).__SC_IS_GESTURE) return;
    } catch {}

    const e = ev as CustomEvent<any>;
    const target = e.detail?.section || section;
    if (target !== section) return;

    const becameMuted =
      ev.type === "sc:autoplay-muted" || ev.type === "sc:autoplay-failed-unmute";

    if (becameMuted) {
      video.muted = true;
      section.setAttribute("data-muted", "1");
      updateMuteButtonUI(section, true);
    }
  };

  document.addEventListener("sc:autoplay-muted", handleAutoplayStatus as EventListener, { passive: true });
  document.addEventListener("sc:autoplay-failed-unmute", handleAutoplayStatus as EventListener, { passive: true });

  let hudTimer: number | undefined;

  const hide = () => {
    controls.classList.remove("hud-active");
  };

  const show = (ms: number = wakeShowMs) => {
    controls.classList.add("hud-active");

    if (hudTimer) clearTimeout(hudTimer);
    hudTimer = window.setTimeout(hide, ms);
  };

  const showPersistent = () => {
    if (hudTimer) clearTimeout(hudTimer);
    controls.classList.add("hud-active");
  };

  const onHudWake = (e: any) => {
    if (e.detail?.section === section) showPersistent();
  };
  document.addEventListener("sc:hud-wake", onHudWake);

  const userPref = readCcPref();
  const autoplayMuted = section.getAttribute("data-muted") === "1";
  const applyCaptions = (typeof userPref === "boolean") ? userPref : autoplayMuted;
  applyCcState(section, applyCaptions);
  captionsApi.mountIfNeeded();

  (async () => {
    const disableAutoplay = opts && (opts as any).disableAutoplay === true;
    try {
      if (!disableAutoplay) {
        await initAutoplay(video, section, markAsSeenOnAttach);
      }
    } finally {
      requestAnimationFrame(() => {
        updateMuteButtonUI(section, video.muted);
      });
    }
  })();

  let wakeHandle: { destroy: () => void } | null = null;
  if (!disableWakeZone) {
    wakeHandle = createWakeZone(section, controls, video, show, tapMoveThresholdPx, tapMaxMs);
  }

  const onPause = () => {
    if (hudTimer) clearTimeout(hudTimer);
    showPersistent();
  };
  const onPlay = () => {
    pauseAllOtherMedia(video);
    show(wakeShowMs);
  };
  const onEnded = () => {
    video.pause();
    showPersistent();
  };
  const onVolumeChange = () => {
    updateMuteButtonUI(section, video.muted);
  };

  const optsListener: AddEventListenerOptions = { passive: true };
  video.addEventListener("pause", onPause, optsListener);
  video.addEventListener("play", onPlay, optsListener);
  video.addEventListener("ended", onEnded, optsListener);
  video.addEventListener("volumechange", onVolumeChange, optsListener);

  if (markAsSeenOnAttach) {
    requestAnimationFrame(() => show(initialShowMs));
  }

  const destroy = () => {
    if (hudTimer) clearTimeout(hudTimer);
    video.removeEventListener("pause", onPause);
    video.removeEventListener("play", onPlay);
    video.removeEventListener("ended", onEnded);
    video.removeEventListener("volumechange", onVolumeChange);
    document.removeEventListener("sc:hud-wake", onHudWake);
    document.removeEventListener("sc:autoplay-muted", handleAutoplayStatus as EventListener);
    document.removeEventListener("sc:autoplay-failed-unmute", handleAutoplayStatus as EventListener);
    captionsApi.unmountIfPresent();
    if (wakeHandle) {
      try {
        wakeHandle.destroy();
      } catch {}
      wakeHandle = null;
    }
  };

  return {
    root: controls,
    show,
    showPersistent,
    hide,
    destroy,
    setHudVisibility: (buttons: Partial<Record<"pause" | "mute" | "cc" | "like", boolean>> = {}) => {
      if (buttons == null) {
        controls.classList.add("hud-active");
        return;
      }
      const anyVisible = Object.values(buttons).some(Boolean);
      controls.classList.toggle("hud-active", anyVisible);
    },
  };
}
